import { z } from 'zod';
import {
  DEFECT_RESPONSIBILITIES,
  HANDOVER_CHANNELS,
  HANDOVER_CLEANING,
  HANDOVER_CONDITIONS,
  HANDOVER_DIRECTIONS,
  HANDOVER_ELEMENTS,
  HANDOVER_LIMITS as L,
  HANDOVER_ROLES,
  HANDOVER_ROOM_KINDS,
  SIGNATURE_BOX,
  SIGNATURE_METHODS,
  handoverPhotoIds,
} from '../handover';
import { zAddress, zCents, zDate, zId, zReason } from './common';
import { zHandoverKey } from './tenancies';

/** Ids of rooms, items and attendees inside one protocol (created by the clients, stable across versions). */
const zLocalId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, { message: 'Ungültige Id' });
const zPhotoIds = z.array(zId).max(L.photosPerSpot).default([]);
const text = (max: number) => z.string().trim().max(max);

export const zHandoverDefect = z.object({
  description: text(1000).default(''),
  responsibility: z.enum(DEFECT_RESPONSIBILITIES).default('open'),
  /** Until when the tenant fixes it (agreed in the protocol). */
  repairBy: zDate.optional(),
  /** Estimated cost (deduction suggestion for the deposit settlement). */
  estimatedCostCents: zCents.min(0).max(10_000_000).optional(),
  /** Already there at the move-in (not the tenant's damage). */
  preexisting: z.boolean().default(false),
});
export const zHandoverItem = z.object({
  id: zLocalId,
  element: z.enum(HANDOVER_ELEMENTS),
  label: text(80).optional(),
  condition: z.enum(HANDOVER_CONDITIONS).nullable().default(null),
  note: text(1000).optional(),
  photoIds: zPhotoIds,
  defect: zHandoverDefect.optional(),
});
export const zHandoverRoom = z.object({
  id: zLocalId,
  kind: z.enum(HANDOVER_ROOM_KINDS),
  label: text(60).min(1),
  note: text(2000).optional(),
  photoIds: zPhotoIds,
  items: z.array(zHandoverItem).max(L.itemsPerRoom).default([]),
});
export const zHandoverMeter = z.object({
  meterId: zId,
  value: z.number().min(0).max(1e10).nullable().default(null),
  photoId: zId.nullable().default(null),
  note: text(300).optional(),
});
export const zHandoverAttendee = z.object({
  id: zLocalId,
  role: z.enum(HANDOVER_ROLES),
  name: text(120).min(1),
  /** The contract party this attendee is (tenants); representatives and witnesses have none. */
  personId: zId.optional(),
});

const unique = <T>(list: T[], key: (x: T) => string) => new Set(list.map(key)).size === list.length;

/**
 * Editable content of a draft (PUT replaces all of it, If-Match: version). Signatures are not part of it: they are given
 * one by one (POST /handovers/:id/signatures) and fall away when the content changes afterwards.
 */
export const HandoverDraftBody = z
  .object({
    date: zDate,
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().default(null),
    attendees: z.array(zHandoverAttendee).max(L.attendees).default([]),
    rooms: z.array(zHandoverRoom).max(L.rooms).default([]),
    meters: z.array(zHandoverMeter).max(L.meters).default([]),
    keys: z.array(zHandoverKey).max(30).default([]),
    cleaning: z.enum(HANDOVER_CLEANING).nullable().default(null),
    /** Vereinbarungen (z. B. "Mieter streicht das Kinderzimmer bis 15.11."). */
    agreements: text(5000).default(''),
    notes: text(5000).default(''),
    photoIds: z.array(zId).max(60).default([]),
    /** New address of the tenants (move-out): needed for the statement and the deposit. */
    forwardingAddress: zAddress.nullable().default(null),
    /** Take the forwarding address over as the tenants' postal address when completing. */
    applyForwardingAddress: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const ids = [...v.rooms.map((r) => r.id), ...v.rooms.flatMap((r) => r.items.map((i) => i.id))];
    if (!unique(ids, (x) => x)) ctx.addIssue({ code: 'custom', message: 'Doppelte Id in Räumen', path: ['rooms'] });
    if (!unique(v.attendees, (a) => a.id)) ctx.addIssue({ code: 'custom', message: 'Doppelte Id bei Anwesenden', path: ['attendees'] });
    if (!unique(v.meters, (m) => m.meterId)) ctx.addIssue({ code: 'custom', message: 'Zähler doppelt', path: ['meters'] });
    if (handoverPhotoIds(v).length > L.photosTotal) ctx.addIssue({ code: 'custom', message: `Höchstens ${L.photosTotal} Fotos je Protokoll`, path: ['photoIds'] });
    v.attendees.forEach((a, i) => {
      if (a.personId && a.role !== 'tenant') ctx.addIssue({ code: 'custom', message: 'Nur Mieter sind Vertragspartei', path: ['attendees', i, 'personId'] });
    });
  });

export const CreateHandoverBody = z.object({
  tenancyId: zId,
  direction: z.enum(HANDOVER_DIRECTIONS),
  date: zDate.optional(),
  /** Replaces a completed protocol of the same tenancy and direction (the old one stays, marked as replaced). */
  replacesId: zId.optional(),
  reason: zReason.optional(),
});

const zStroke = z
  .array(z.number().int().min(0).max(SIGNATURE_BOX.w))
  .min(2)
  .max(L.strokePoints * 2)
  .refine((s) => s.length % 2 === 0 && s.every((v, i) => i % 2 === 0 || v <= SIGNATURE_BOX.h), { message: 'Ungültige Unterschrift' });
export const HandoverSignatureBody = z
  .object({
    attendeeId: zLocalId,
    method: z.enum(SIGNATURE_METHODS),
    strokes: z.array(zStroke).min(1).max(L.strokes).optional(),
    note: text(300).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'drawn' && !v.strokes?.length) ctx.addIssue({ code: 'custom', message: 'Unterschrift fehlt', path: ['strokes'] });
    if (v.method !== 'drawn' && v.strokes) ctx.addIssue({ code: 'custom', message: 'Keine Unterschrift bei abwesend/verweigert', path: ['strokes'] });
  });

export const CompleteHandoverBody = z.object({ preview: z.boolean().default(false) });
/** Upload of a protocol photo (JPEG/PNG; the PDF embeds them) or of the signed paper protocol (PDF or photo). */
export const HandoverFileBody = z
  .object({
    purpose: z.enum(['photo', 'paper']).default('photo'),
    mime: z.enum(['image/jpeg', 'image/png', 'application/pdf']),
    size: z.number().int().positive().max(25 * 1024 * 1024, { message: 'Datei größer als 25 MB' }),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    originalName: z.string().max(200).default(''),
  })
  .refine((v) => v.purpose === 'paper' || v.mime !== 'application/pdf', { message: 'Fotos als JPEG oder PNG', path: ['mime'] });
export const HandoverPaperBody = z.object({ documentId: zId.nullable() });
export const HandoverAddendumBody = z.object({ text: text(2000).min(5) });
export const HandoverDispatchBody = z.object({ channels: z.array(z.enum(HANDOVER_CHANNELS)).min(1).max(2) });
